import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { usePurchaseSubscriptionContext } from '../PurchaseSubscription/context/PurchaseSubscriptionContext';
import { centsToDollarsNumber, getLocalizedPricing } from '../../lib/util';
import { gqlTypes } from '../../types/gql';
import { useLocale } from '../../contexts/LocaleContext';
import * as types from '../../types';

function subscriptionOptionName(months: number, subscriptionOption: gqlTypes.subscriptionOption, t: any) {
  const miniPlus =
    subscriptionOption.isPlusPlan && subscriptionOption.sku.startsWith('sub-m1') ? t('mini_plus') + ' ' : '';

  if (months % 12 === 0) {
    const years = months / 12;
    return t('years_year_mini_plus_membership', { years, miniPlus });
  }

  return t('months_month_mini_plus_membership', { months, miniPlus });
}

export default function usePrepaidSubscriptionOption() {
  const { t } = useTranslation();
  const { device, subscriptionOptions } = usePurchaseSubscriptionContext();
  // Use Redux cart currency which is updated when billing country changes.
  // Default to USD for users with stale localStorage state that lacks the currency field.
  const currency = useSelector((state: types.AppState) => state.subscriptionShop.cart.currency) ?? types.FiCurrency.USD;
  // Use user's locale for display formatting (determines when to show currency suffix)
  const { currencyFormatter } = useLocale();

  return useMemo(() => {
    const prepaidSubscription = device.prepaidSubscription;

    const devicePreselectedPlanCode = prepaidSubscription?.planCode;
    if (!devicePreselectedPlanCode) {
      return null;
    }

    const subscriptionOption = subscriptionOptions.find((option) => option.sku === devicePreselectedPlanCode);
    if (!subscriptionOption) {
      return null;
    }

    // Get the price in the user's currency. For prepaid, the reseller price takes priority if available.
    const firstTermPriceInCents =
      prepaidSubscription.resellerPriceInCents ?? getLocalizedPricing(subscriptionOption.pricingInCents, currency);
    const firstTermFormattedPrice = currencyFormatter.format(centsToDollarsNumber(firstTermPriceInCents)) ?? '';

    const renewalPlanCode = device.prepaidSubscription?.renewalPlanCode;
    const renewalSubscriptionOption = renewalPlanCode
      ? subscriptionOptions.find((option) => option.sku === renewalPlanCode) ?? subscriptionOption
      : subscriptionOption;

    const renewalPriceInCents = getLocalizedPricing(renewalSubscriptionOption.pricingInCents, currency);
    const renewalFormattedPrice = currencyFormatter.format(centsToDollarsNumber(renewalPriceInCents)) ?? '';

    const firstTermMonths = subscriptionOption.renewalMonths + (prepaidSubscription.freeMonths ?? 0);
    const nextBillingDate = DateTime.now().plus({ months: firstTermMonths }).toFormat('MM/dd/yyyy');

    return {
      subscriptionOptionSku: subscriptionOption.sku,
      subscriptionOptionName: subscriptionOptionName(firstTermMonths, subscriptionOption, t),
      formattedPrice: firstTermFormattedPrice,
      renewalSubscriptionOption,
      renewalFormattedPrice,
      nextBillingDate,
      orderSource: prepaidSubscription.orderSource,
    };
  }, [device.prepaidSubscription, subscriptionOptions, t, currency, currencyFormatter]);
}
